// src/components/mobileMenus.js
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { uploadMenuImage } from "../../utils/api";
import Tables from "../Tables";
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaTimes,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaBars,
  FaImage,
} from "react-icons/fa";

export default function MenusScreen() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm] = useState({});
  const [itemImageFile, setItemImageFile] = useState(null);
  const [itemUploading, setItemUploading] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const toastTimeout = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [search, setSearch] = useState("");
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuModalMode, setMenuModalMode] = useState("add");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingMenuId, setEditingMenuId] = useState(null);
  const [editingMenuName, setEditingMenuName] = useState("");

  useEffect(() => {
    fetchMenus();
  }, []);

  async function fetchMenus() {
    setLoading(true);
    try {
      const res = await api.get("/menus");
      setMenus(res.data);
      if (res.data.length > 0 && !activeMenuId) {
        setActiveMenuId(res.data[0].id);
      }
    } catch (err) {
      showToast("Failed to load menus", "error");
    }
    setLoading(false);
  }

  async function handleCreateMenu(e) {
    e.preventDefault();
    try {
      await api.post("/menus", { name: newCategoryName });
      setNewCategoryName("");
      fetchMenus();
      showToast("Category added!");
      setShowMenuModal(false);
    } catch (err) {
      showToast("Failed to create category", "error");
    }
  }

  async function handleUpdateMenu() {
    try {
      await api.put(`/menus/${editingMenuId}`, { name: editingMenuName });
      setEditingMenuId(null);
      setEditingMenuName("");
      fetchMenus();
      showToast("Category updated!");
      setShowMenuModal(false);
    } catch (err) {
      showToast("Failed to update category", "error");
    }
  }

  async function handleDeleteMenu(id) {
    if (!window.confirm("Delete this category and all its items?")) return;
    try {
      await api.delete(`/menus/${id}`);
      fetchMenus();
      showToast("Category deleted!");
    } catch (err) {
      showToast("Failed to delete category", "error");
    }
  }

  function openItemModal(menuId, item = null) {
    setActiveMenuId(menuId);
    setItemForm(
      item
        ? { ...item }
        : {
            name: "",
            price: "",
            description: "",
            imageUrl: "",
            active: true,
          }
    );
    setItemImageFile(null);
    setShowItemModal(true);
  }

  function closeItemModal() {
    setShowItemModal(false);
    setItemForm({});
    setItemImageFile(null);
  }

  async function handleItemImageUpload(file) {
    if (!file) return "";
    setItemUploading(true);
    try {
      // Normalize to a File (camera blobs sometimes lack a name)
      let uploadFile = file;
      try {
        if (!(file instanceof File)) {
          uploadFile = new File([file], file.name || `photo_${Date.now()}.jpg`, {
            type: file.type || "image/jpeg",
          });
        } else if (!file.name) {
          uploadFile = new File([file], `photo_${Date.now()}.jpg`, {
            type: file.type || "image/jpeg",
          });
        }
      } catch (e) {
        // fallback: keep original file if File constructor unsupported
        uploadFile = file;
      }

      // 1) Try existing helper (keeps existing behavior / CDN helpers)
      try {
        const url = await uploadMenuImage(uploadFile);
        if (url) {
          setItemUploading(false);
          return url;
        }
      } catch (helperErr) {
        console.warn("uploadMenuImage helper failed, falling back to multipart:", helperErr);
      }

      // 2) Fallback: multipart/form-data POST — let axios set the Content-Type (boundary)
      const formData = new FormData();
      formData.append("file", uploadFile); // most backends expect 'file'
      // some backends expect 'image' — if your backend expects that, uncomment:
      // formData.append("image", uploadFile);

      const res = await api.post("/upload", formData); // don't force Content-Type header
      setItemUploading(false);
      // handle common response shapes
      return res.data?.url || res.data?.path || res.data?.fileUrl || res.data?.secure_url || "";
    } catch (err) {
      setItemUploading(false);
      console.error("handleItemImageUpload error:", err);
      showToast("Image upload failed", "error");
      throw err;
    }
  }

  async function handleSaveItem() {
    let imageUrl = itemForm.imageUrl;
    if (itemImageFile) {
      imageUrl = await handleItemImageUpload(itemImageFile);
    }
    const itemData = { ...itemForm, imageUrl };
    try {
      if (itemForm.id) {
        await api.put(`/menus/${activeMenuId}/items/${itemForm.id}`, itemData);
        showToast("Item updated!");
      } else {
        await api.post(`/menus/${activeMenuId}/items`, itemData);
        showToast("Item added!");
      }
      closeItemModal();
      fetchMenus();
    } catch (err) {
      showToast("Failed to save item", "error");
    }
  }

  async function handleDeleteItem(menuId, itemId) {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/menus/${menuId}/items/${itemId}`);
      fetchMenus();
      showToast("Item deleted!");
    } catch (err) {
      showToast("Failed to delete item", "error");
    }
  }

  async function handleToggleActive(menuId, itemId) {
    try {
      await api.patch(`/menus/${menuId}/items/${itemId}/active`);
      fetchMenus();
      showToast("Status updated!");
    } catch (err) {
      showToast("Failed to update status", "error");
    }
  }

  function showToast(message, type = "success") {
    setToast({ show: true, message, type });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(
      () => setToast({ show: false, message: "", type: "" }),
      3000
    );
  }

  function handleSelectMenu(menuId) {
    setActiveMenuId(menuId);
    setSidebarOpen(false);
  }

  function openAddMenuModal() {
    setMenuModalMode("add");
    setNewCategoryName("");
    setShowMenuModal(true);
  }

  function openEditMenuModal(id, name) {
    setMenuModalMode("edit");
    setEditingMenuId(id);
    setEditingMenuName(name);
    setShowMenuModal(true);
  }

  function closeMenuModal() {
    setShowMenuModal(false);
    setEditingMenuId(null);
    setEditingMenuName("");
    setNewCategoryName("");
  }

  const selectedMenu = menus.find((m) => m.id === activeMenuId);
  const items = (selectedMenu?.items || []).filter(
    (item) =>
      (!showOutOfStock || !item.active) &&
      (item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description.toLowerCase().includes(search.toLowerCase()))
  );

  const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;
  function getFullImageUrl(imageUrl) {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http")) return imageUrl;
    const cleanUrl = imageUrl.replace(/^\/?.*uploads\//, "");
    return `${BASE_URL}/uploads/${cleanUrl}`;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block relative w-16 h-16">
            <motion.div
              className="absolute inset-0 border-4 border-rose-400 border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="mt-4 text-rose-800 font-medium">Loading menus...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 relative">
      {/* Mobile Header */}
      <div className="bg-white p-4 shadow-sm flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 text-gray-600"
          aria-label="Open categories"
        >
          <FaBars size={20} />
        </button>

        <h1 className="text-lg font-bold text-gray-800">Menu Management</h1>

        {/* Add Category button - mobile friendly */}
        <motion.button
          onClick={() => {
            setMenuModalMode("add");
            setNewCategoryName("");
            setShowMenuModal(true);
          }}
          className="w-10 h-10 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 text-white flex items-center justify-center shadow-md"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Add category"
        >
          <FaPlus size={14} />
        </motion.button>
      </div>

      {/* Search and Filters */}

      <div className="p-4">
        <div className="relative mb-3">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-300"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Current category indicator - compact version */}
        {selectedMenu && (
          <div className="flex items-center justify-between mb-3 p-2 bg-rose-50 rounded-lg">
            <span className="text-sm font-medium text-rose-700">
              Category:{" "}
              <span className="text-gray-800">{selectedMenu.name}</span>
            </span>
            <span className="text-xs bg-white px-2 py-1 rounded-full text-rose-600 font-medium">
              {items.length} items
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showOutOfStock}
              onChange={(e) => setShowOutOfStock(e.target.checked)}
              className="form-checkbox h-4 w-4 text-rose-500 rounded"
            />
            <span className="text-sm text-gray-700">Show out of stock</span>
          </label>

          <motion.button
            onClick={() => selectedMenu && openItemModal(selectedMenu.id)}
            className="px-4 py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-full flex items-center gap-2 text-sm"
            disabled={!selectedMenu}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPlus size={14} />
            <span>Add Item</span>
          </motion.button>
        </div>
      </div>

      {/* Category Header */}
      {selectedMenu && (
        <div className="px-4 mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            {selectedMenu.name}
          </h2>
          <p className="text-sm text-gray-500">
            {items.length} {items.length === 1 ? "item" : "items"}
          </p>
        </div>
      )}

      {/* Items List */}
      <div className="px-4 pb-20">
        {!selectedMenu ? (
          <div className="text-center py-12 text-gray-400">
            Select a category to view items
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            {search || showOutOfStock
              ? "No matching items found"
              : "No items in this category"}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
              >
                <div className="flex gap-3">
                  {item.imageUrl && (
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={getFullImageUrl(item.imageUrl)}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-semibold text-gray-800 truncate">
                        {item.name}
                      </h3>
                      <span className="font-bold text-rose-600 ml-2">
                        ₹{item.price}
                      </span>
                    </div>

                    {item.description && (
                      <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                        {item.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openItemModal(selectedMenu.id, item)}
                          className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                          aria-label="Edit item"
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteItem(selectedMenu.id, item.id)
                          }
                          className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                          aria-label="Delete item"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs ${
                            item.active ? "text-green-600" : "text-gray-500"
                          }`}
                        >
                          {item.active ? "Active" : "Inactive"}
                        </span>
                        <button
                          onClick={() =>
                            handleToggleActive(selectedMenu.id, item.id)
                          }
                          className="p-1"
                          aria-label={
                            item.active ? "Deactivate item" : "Activate item"
                          }
                        >
                          {item.active ? (
                            <FaToggleOn className="text-green-500 text-lg" />
                          ) : (
                            <FaToggleOff className="text-gray-400 text-lg" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Sidebar/Drawer */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setSidebarOpen(false)}
            />

            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25 }}
              className="absolute left-0 top-0 h-full w-64 bg-white z-50 shadow-xl rounded-r-xl"
            >
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                    Categories
                    <button
                      onClick={openAddMenuModal}
                      className="p-1 text-rose-500 hover:text-rose-600"
                      aria-label="Add category"
                    >
                      <FaPlus size={16} />
                    </button>
                  </h2>
                  {/* <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 text-gray-500"
                  >
                    <FaTimes size={18} />
                  </button> */}
                </div>
              </div>

              <div className="p-4 overflow-y-auto h-full pb-20">
                <div className="space-y-2">
                  {menus.map((menu) => (
                    <div
                      key={menu.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        activeMenuId === menu.id
                          ? "bg-gradient-to-r from-rose-100 to-amber-100 border border-rose-200"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => handleSelectMenu(menu.id)}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`font-medium ${
                            activeMenuId === menu.id
                              ? "text-rose-700"
                              : "text-gray-700"
                          }`}
                        >
                          {menu.name}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditMenuModal(menu.id, menu.name);
                            }}
                            className="p-1 text-rose-400 hover:text-rose-600"
                            aria-label="Edit category"
                          >
                            <FaEdit size={12} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteMenu(menu.id);
                            }}
                            className="p-1 text-rose-400 hover:text-rose-600"
                            aria-label="Delete category"
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Item Modal */}
      <AnimatePresence>
        {showItemModal && (
          <>
            {/* overlay raised above app nav (z-60) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              // ensure overlay is on top of app nav and not clipped
              className="fixed inset-0 bg-black bg-opacity-50 z-[9999]"
              onClick={closeItemModal}
            />

            {/* centered responsive modal wrapper */}
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              /* wrapper above overlay and app nav */
              className="fixed inset-0 z-[10000] flex items-center justify-center p-3"
            >
              <div
                onClick={(e) => e.stopPropagation()}
                /* responsive inner container (full width on small devices) */
                className="w-full max-w-[420px] mx-auto bg-white rounded-2xl shadow-xl z-[10001] max-h-[90vh] overflow-y-auto"
                style={{ boxSizing: "border-box" }}
              >
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg text-gray-800">
                      {itemForm.id ? "Edit Item" : "Add Item"}
                    </h3>
                    <button onClick={closeItemModal} className="p-2">
                      <FaTimes size={18} />
                    </button>
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Item Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
                      value={itemForm.name || ""}
                      onChange={(e) =>
                        setItemForm({ ...itemForm, name: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
                      value={itemForm.price || ""}
                      onChange={(e) =>
                        setItemForm({ ...itemForm, price: e.target.value })
                      }
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
                      rows={3}
                      value={itemForm.description || ""}
                      onChange={(e) =>
                        setItemForm({
                          ...itemForm,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image
                    </label>

                    {itemImageFile ? (
                      <div className="mb-4 text-center">
                        <img
                          src={URL.createObjectURL(itemImageFile)}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-lg mx-auto mb-2 shadow-md"
                        />
                        <p className="text-xs text-gray-500 truncate">
                          {itemImageFile.name}
                        </p>
                      </div>
                    ) : itemForm.imageUrl ? (
                      <div className="mb-4 text-center">
                        <img
                          src={getFullImageUrl(itemForm.imageUrl)}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-lg mx-auto mb-2 shadow-md"
                        />
                      </div>
                    ) : (
                      <div className="mb-4 text-center text-gray-400">
                        <FaImage className="text-4xl mx-auto mb-2" />
                        <p className="text-sm">No image selected</p>
                        <p className="text-xs text-gray-400">
                          Recommended: 500x500px
                        </p>
                      </div>
                    )}

                    {/* Buttons for mobile */}
                    <div className="flex gap-2">
                      {/* Take Photo */}
                      <label className="flex-1 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-center cursor-pointer shadow-md">
                        Take Photo
                        <input
                          name="file"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) =>
                            e.target.files[0] &&
                            setItemImageFile(e.target.files[0])
                          }
                        />
                      </label>

                      {/* Choose from Gallery */}
                      <label className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-center cursor-pointer shadow-md">
                        Choose File
                        <input
                          name="file"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) =>
                            e.target.files[0] &&
                            setItemImageFile(e.target.files[0])
                          }
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Active
                    </label>
                    <button
                      onClick={() =>
                        setItemForm({ ...itemForm, active: !itemForm.active })
                      }
                      className="p-1"
                    >
                      {itemForm.active ? (
                        <FaToggleOn className="text-green-500 text-2xl" />
                      ) : (
                        <FaToggleOff className="text-gray-400 text-2xl" />
                      )}
                    </button>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={closeItemModal}
                      className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-lg font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveItem}
                      disabled={itemUploading}
                      className="flex-1 py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-medium disabled:opacity-50"
                    >
                      {itemUploading
                        ? "Uploading..."
                        : itemForm.id
                        ? "Update"
                        : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Category Modal */}
      <AnimatePresence>
        {showMenuModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={closeMenuModal}
            />

            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm mx-auto bg-white rounded-2xl p-6 z-60 max-h-[90vh] overflow-y-auto"
              >
                <h3 className="font-bold text-lg text-gray-800 mb-4">
                  {menuModalMode === "add" ? "Add Category" : "Edit Category"}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
                      value={
                        menuModalMode === "add"
                          ? newCategoryName
                          : editingMenuName
                      }
                      onChange={(e) =>
                        menuModalMode === "add"
                          ? setNewCategoryName(e.target.value)
                          : setEditingMenuName(e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={closeMenuModal}
                      className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={
                        menuModalMode === "add"
                          ? handleCreateMenu
                          : handleUpdateMenu
                      }
                      className="flex-1 py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-medium"
                    >
                      {menuModalMode === "add" ? "Add" : "Update"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 px-4 py-3 rounded-lg shadow-lg font-medium text-sm ${
              toast.type === "error"
                ? "bg-red-500 text-white"
                : "bg-green-500 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="w-full h-full bg-gray-50 text-gray-900 flex flex-col top-0 left-0 right-0 bottom-0">
        <Tables />
      </div>
    </div>
  );
}
